import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Icon } from '../components/icons';
import { Badge, Button, Card, Field, Input, Spinner } from '../components/ui';
import type { ShareLinkDto } from '../lib/types';

/**
 * Named share links, with an optional access code and a per-link response cap.
 *
 * The one thing the copy here has to be honest about is what an access code protects: the
 * link, and — because the bare form URL closes once every active link is protected — the
 * form as well. Somebody reasonably assumes "password" means the second, and would be
 * right only because of that rule, so the UI says so rather than leaving it to be
 * discovered.
 */
export function ShareLinks({ formId }: { formId: string }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [maxResponses, setMaxResponses] = useState('');
  const [open, setOpen] = useState(false);

  const links = useQuery({
    queryKey: ['links', formId],
    queryFn: () => api.links.list(formId),
  });

  const create = useMutation({
    mutationFn: () => api.links.create(formId, {
      name: name.trim(),
      password: password.trim() || undefined,
      maxResponses: maxResponses ? Number(maxResponses) : undefined,
    }),
    onSuccess: () => {
      setName(''); setPassword(''); setMaxResponses(''); setOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['links', formId] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.links.remove(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['links', formId] }),
  });

  const data = links.data?.data ?? [];

  return (
    <Card className="p-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-1.5 text-sm font-semibold">
            <Icon name="link" size={15} />
            Share links
          </h2>
          <p className="mt-1 text-xs text-[var(--ff-text-muted)]">
            A friendly URL with its own schedule, response cap and optional access code.
          </p>
        </div>
        <Button onClick={() => setOpen((v) => !v)}>
          <Icon name="plus" size={14} /> New link
        </Button>
      </div>

      {open && (
        <div className="mb-4 rounded-md border border-[var(--ff-border)]
                        bg-[var(--ff-surface-sunken)] p-3">
          <Field label="Name" hint="Becomes the slug, so /s/staff-survey-2026.">
            <Input
              value={name}
              placeholder="Staff survey 2026"
              onChange={(e) => setName(e.target.value)}
            />
          </Field>

          <div className="mt-3">
            <Field
              label="Access code (optional)"
              hint="Shared with everyone who gets the link, so treat it as a doormat key, not
                    a password. While every active link has one, the bare /f/ URL for this
                    form closes too — otherwise the code would protect nothing."
            >
              <Input
                type="text"
                value={password}
                placeholder="Leave blank for an open link"
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
          </div>

          <div className="mt-3">
            <Field
              label="Response cap (optional)"
              hint="Counted exactly. The link closes itself once it is reached."
            >
              <Input
                type="number"
                min={1}
                value={maxResponses}
                placeholder="Unlimited"
                onChange={(e) => setMaxResponses(e.target.value)}
              />
            </Field>
          </div>

          <div className="mt-3 flex items-center gap-3">
            <Button
              variant="primary"
              onClick={() => create.mutate()}
              disabled={create.isPending || name.trim().length < 3}
            >
              {create.isPending ? 'Creating…' : 'Create link'}
            </Button>
            {create.error && (
              <span className="text-sm text-[var(--ff-danger)]">
                {(create.error as Error).message}
              </span>
            )}
          </div>
        </div>
      )}

      {links.isLoading && <Spinner label="Loading links" />}

      {!links.isLoading && data.length === 0 && (
        <p className="text-sm text-[var(--ff-text-muted)]">
          No share links yet. The form is still reachable at its direct URL.
        </p>
      )}

      <ul className="space-y-2">
        {data.map((link) => (
          <LinkRow key={link.id} link={link} onRemove={() => remove.mutate(link.id)} />
        ))}
      </ul>
    </Card>
  );
}

function LinkRow({ link, onRemove }: { link: ShareLinkDto; onRemove: () => void }) {
  const [copied, setCopied] = useState(false);
  const full = link.maxResponses != null && link.remaining === 0;

  return (
    <li className="rounded-md border border-[var(--ff-border)] p-3">
      <div className="flex items-center gap-2">
        <code className="flex-1 truncate text-sm">/s/{link.slug}</code>

        {link.hasPassword && <Badge tone="primary">code</Badge>}
        {full && <Badge tone="warning">full</Badge>}
        {!link.isActive && <Badge tone="neutral">off</Badge>}

        <button
          type="button"
          className="rounded p-1 text-[var(--ff-text-muted)] hover:text-[var(--ff-text)]"
          aria-label={`Copy the link for ${link.slug}`}
          onClick={() => {
            void navigator.clipboard.writeText(link.url);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
        >
          <Icon name={copied ? 'check' : 'copy'} size={15} />
        </button>

        <button
          type="button"
          className="rounded p-1 text-[var(--ff-danger)] hover:bg-[var(--ff-danger-soft)]"
          aria-label={`Deactivate the link for ${link.slug}`}
          onClick={onRemove}
        >
          <Icon name="trash" size={15} />
        </button>
      </div>

      <p className="mt-1 text-xs text-[var(--ff-text-muted)]">
        {link.responseCount} response{link.responseCount === 1 ? '' : 's'}
        {link.maxResponses != null && ` of ${link.maxResponses}`}
        {link.closesUtc && ` · closes ${new Date(link.closesUtc).toLocaleDateString()}`}
        {' · '}code {link.shortCode}
      </p>
    </li>
  );
}
